import { Button, Input } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { DatePicker } from '@/components/ui/DatePicker';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const contractSchema = (t: any) =>
  z
    .object({
      companyName: z.string().min(1, t('contracts.form.companyNameRequired')),
      contractNumber: z.string().min(1, t('contracts.form.contractNumberRequired')),
      startDate: z.date({
        required_error: t('contracts.form.startDateRequired'),
        invalid_type_error: t('contracts.form.invalidDate'),
      }),
      endDate: z.date({
        required_error: t('contracts.form.endDateRequired'),
        invalid_type_error: t('contracts.form.invalidDate'),
      }),
      contractedHours: z
        .string()
        .min(1, t('contracts.form.contractedHoursRequired'))
        .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
          message: t('contracts.form.invalidHours'),
        }),
      monthlyHoursLimit: z
        .string()
        .optional()
        .refine((val) => !val || (!isNaN(Number(val)) && Number(val) >= 0), {
          message: t('contracts.form.invalidHours'),
        }),
      minDebitTime: z
        .string()
        .min(1, t('contracts.form.minDebitTimeRequired'))
        .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
          message: t('contracts.form.invalidMinutes'),
        }),
      accumulateHours: z.boolean().optional(),
      excludeProactiveTickets: z.boolean().optional(),
      archived: z.boolean().optional(),
    })
    .refine((data) => data.endDate > data.startDate, {
      message: t('contracts.form.endDateAfterStartDate'),
      path: ['endDate'],
    });

export type ContractFormData = z.infer<ReturnType<typeof contractSchema>>;

export interface CreateContractFormProps {
  mode: 'create';
  onSubmit: (data: ContractFormData) => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
}

export interface UpdateContractFormProps {
  mode: 'update';
  onSubmit: (data: ContractFormData) => Promise<void>;
  isLoading: boolean;
  initialData: {
    companyName: string;
    contractNumber: string;
    startDate?: Date;
    endDate?: Date;
    contractedHours: string;
    monthlyHoursLimit?: string;
    minDebitTime: string;
    accumulateHours?: boolean;
    excludeProactiveTickets?: boolean;
    archived?: boolean;
  };
  onCancel: () => void;
}

export type ContractFormProps = CreateContractFormProps | UpdateContractFormProps;

export function ContractForm(props: ContractFormProps) {
  const { mode, onSubmit, isLoading, onCancel } = props;
  const initialData = props.mode === 'update' ? props.initialData : undefined;

  const { t } = useTranslation();

  const isCreate = mode === 'create';
  const schema = contractSchema(t);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: isCreate
      ? {
          companyName: '',
          contractNumber: '',
          startDate: undefined,
          endDate: undefined,
          contractedHours: '',
          monthlyHoursLimit: '',
          minDebitTime: '15',
          accumulateHours: false,
          excludeProactiveTickets: false,
        }
      : {
          companyName: initialData?.companyName || '',
          contractNumber: initialData?.contractNumber || '',
          startDate: initialData?.startDate,
          endDate: initialData?.endDate,
          contractedHours: initialData?.contractedHours || '',
          monthlyHoursLimit: initialData?.monthlyHoursLimit || '',
          minDebitTime: initialData?.minDebitTime || '15',
          accumulateHours: initialData?.accumulateHours || false,
          excludeProactiveTickets: initialData?.excludeProactiveTickets || false,
          archived: initialData?.archived || false,
        },
  });

  const handleFormSubmit = async (data: any) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="gap-md flex flex-col">
      <Input
        {...register('companyName')}
        label={t('contracts.form.companyName')}
        placeholder={t('contracts.form.companyNamePlaceholder')}
        error={errors.companyName?.message}
        disabled={isLoading}
      />
      <Input
        {...register('contractNumber')}
        label={t('contracts.form.contractNumber')}
        placeholder={t('contracts.form.contractNumberPlaceholder')}
        error={errors.contractNumber?.message}
        disabled={isLoading}
      />
      <div className="gap-md grid grid-cols-2">
        <DatePicker
          label={t('contracts.form.startDate')}
          placeholder={t('contracts.form.startDatePlaceholder')}
          value={watch('startDate')}
          onChange={(date) => setValue('startDate', date as Date)}
          error={errors.startDate?.message}
          inputDisabled={isLoading}
        />
        <DatePicker
          label={t('contracts.form.endDate')}
          placeholder={t('contracts.form.endDatePlaceholder')}
          value={watch('endDate')}
          onChange={(date) => setValue('endDate', date as Date)}
          error={errors.endDate?.message}
          inputDisabled={isLoading}
          disabled={watch('startDate') ? { before: watch('startDate') as Date } : undefined}
        />
      </div>
      <div className="gap-md grid grid-cols-2">
        <Input
          {...register('contractedHours')}
          type="number"
          label={t('contracts.form.contractedHours')}
          placeholder={t('contracts.form.contractedHoursPlaceholder')}
          error={errors.contractedHours?.message}
          disabled={isLoading}
        />
        <Input
          {...register('monthlyHoursLimit')}
          type="number"
          label={t('contracts.form.monthlyHoursLimit')}
          placeholder={t('contracts.form.monthlyHoursLimitPlaceholder')}
          error={errors.monthlyHoursLimit?.message}
          disabled={isLoading}
        />
      </div>
      <Input
        {...register('minDebitTime')}
        type="number"
        label={t('contracts.form.minDebitTime')}
        placeholder={t('contracts.form.minDebitTimePlaceholder')}
        error={errors.minDebitTime?.message}
        disabled={isLoading}
      />
      <Controller
        name="accumulateHours"
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={field.value}
            onCheckedChange={field.onChange}
            label={t('contracts.form.accumulateHours')}
            name="accumulateHours"
            disabled={isLoading}
          />
        )}
      />
      <Controller
        name="excludeProactiveTickets"
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={field.value}
            onCheckedChange={field.onChange}
            label={t('contracts.form.excludeProactiveTickets')}
            name="excludeProactiveTickets"
            disabled={isLoading}
          />
        )}
      />
      {!isCreate && (
        <Controller
          name="archived"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              label={t('contracts.form.archived')}
              name="archived"
              disabled={isLoading}
            />
          )}
        />
      )}

      <footer className="gap-sm mt-2xl flex self-end">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {t(isCreate ? 'common.create' : 'common.save')}
        </Button>
      </footer>
    </form>
  );
}
